/**
 * Roughly how long a batch will occupy the GPU.
 *
 * Calibrated against two real measurements:
 *
 *   60s audio,  4 covers, with separation -> 123s  (from the API documentation)
 *   349s audio, 2 covers, no separation   ->  86s  (first real order, measured)
 *
 * Those two look contradictory until you notice the cost is barely related to
 * track length: ACE-Step runs a FIXED number of diffusion steps, so most of the
 * time is per-cover overhead rather than per-second-of-audio. An earlier version
 * of this file assumed cost scaled with duration and overestimated the real
 * order by 4.5x -- an estimate that wrong is worse than none, because people
 * stop believing it.
 *
 * The model below predicts both measurements within about 15%. It is still
 * shown as a range and still labelled an estimate: two data points is two data
 * points.
 */

/** Mostly model loading and a fixed diffusion-step count. */
const PER_COVER_FIXED_SECONDS = 20
/** The weak remaining dependence on how long the track is. */
const PER_COVER_REALTIME = 0.05
/** Separation runs once per track, not per cover, and does scale with length. */
const SEPARATION_REALTIME = 0.4
/** ACE-Step reloads weights when the model changes between covers. */
const MODEL_SWITCH_SECONDS = 25

export interface BatchEstimate {
  /** Null when no file had a readable duration. */
  lowSeconds: number | null
  highSeconds: number | null
  coversPerFile: number
  totalCovers: number
  /** Files whose duration the browser could not read. */
  unknownDurations: number
}

export interface EstimateInput {
  durations: (number | null)[]
  targetCount: number
  modelCount: number
  separates: boolean
  /**
   * Whether tone_change is above zero.
   *
   * It forces a four-stem separation pass even when no separation was asked
   * for -- it is the EQ that needs the stems, not the covers. Left out of the
   * estimate it costs about a minute a song, which on a twenty-song batch is
   * twenty minutes the estimate never mentions.
   */
  tonal?: boolean
}

export function estimateBatch({
  durations,
  targetCount,
  modelCount,
  separates,
  tonal = false,
}: EstimateInput): BatchEstimate {
  const coversPerFile = Math.max(1, targetCount) * Math.max(1, modelCount)
  const known = durations.filter((d): d is number => typeof d === 'number' && d > 0)
  const unknownDurations = durations.length - known.length

  if (known.length === 0) {
    return {
      lowSeconds: null,
      highSeconds: null,
      coversPerFile,
      totalCovers: coversPerFile * durations.length,
      unknownDurations,
    }
  }

  const perFile = known.map((seconds) => {
    const generation = coversPerFile * (PER_COVER_FIXED_SECONDS + seconds * PER_COVER_REALTIME)
    // Once, not twice: asking for separation and for tonal variance does not
    // run two passes.
    const separation = separates || tonal ? seconds * SEPARATION_REALTIME : 0
    const switching = Math.max(0, modelCount - 1) * MODEL_SWITCH_SECONDS
    return generation + separation + switching
  })

  const total = perFile.reduce((sum, value) => sum + value, 0)
  // Scaled up for the files we could not measure, so the estimate does not
  // quietly ignore them.
  const average = total / known.length
  const withUnknowns = total + average * unknownDurations

  return {
    lowSeconds: Math.round(withUnknowns * 0.7),
    highSeconds: Math.round(withUnknowns * 1.6),
    coversPerFile,
    totalCovers: coversPerFile * durations.length,
    unknownDurations,
  }
}

export function formatDuration(seconds: number): string {
  if (seconds < 90) return `${Math.round(seconds)} sec`
  const minutes = Math.round(seconds / 60)
  if (minutes < 90) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`
}

export function formatRange(estimate: BatchEstimate): string {
  if (estimate.lowSeconds === null || estimate.highSeconds === null) {
    return 'unknown'
  }
  return `${formatDuration(estimate.lowSeconds)} – ${formatDuration(estimate.highSeconds)}`
}
